import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tag } from "@design-system/Tag/Tag";
import { useIdCopy } from "@hooks/useIdCopy";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import classNames from "classnames";
import { ChartOfAccountsRow } from "../utils";
import styles from "../styles.module.scss";

export const AccountNameCell = memo(
    ({ row }: CellContext<ViewTableFeatures, ChartOfAccountsRow, string>) => {
        const { getCopyProps } = useIdCopy({ label: "Chart of Accounts" });
        const showAccountNumbers = useShowAccountNumbers();
        const rowData = row.original;

        const accountsCount = rowData.accounts?.length;

        const predefinedGroup = rowData.section ?? rowData.predefinedType;
        if (predefinedGroup) {
            return (
                <ViewTableCell>
                    <div
                        className={classNames(
                            styles.columnContent,
                            styles.nameColumn,
                        )}
                    >
                        {getPredefinedGroupLabel(predefinedGroup)}
                        {accountsCount ? (
                            <span className={styles.counter}>
                                {accountsCount}
                            </span>
                        ) : null}
                    </div>
                </ViewTableCell>
            );
        }

        if (rowData.classification) {
            return (
                <ViewTableCell>
                    <div
                        className={classNames(
                            styles.columnContent,
                            styles.nameColumn,
                        )}
                    >
                        {rowData.classification}
                        {accountsCount ? (
                            <span className={styles.counter}>
                                {accountsCount}
                            </span>
                        ) : null}
                    </div>
                </ViewTableCell>
            );
        }

        if (rowData.group) {
            return (
                <ViewTableCell>
                    <div
                        className={classNames(
                            styles.columnContent,
                            styles.nameColumn,
                        )}
                    >
                        {rowData.group.name}
                        {accountsCount ? (
                            <span className={styles.counter}>
                                {accountsCount}
                            </span>
                        ) : null}
                    </div>
                </ViewTableCell>
            );
        }

        if (rowData.account) {
            return (
                <ViewTableCell>
                    <div
                        className={classNames(
                            styles.columnContent,
                            styles.nameColumn,
                        )}
                    >
                        <span {...getCopyProps(rowData.account.id)}>
                            {showAccountNumbers && rowData.account.codeText && (
                                <>
                                    <span>{rowData.account.codeText}</span>
                                    {" - "}
                                </>
                            )}
                            {rowData.account.name}{" "}
                            {rowData.account.isDisabled && (
                                <Tag color="neutral">Archived</Tag>
                            )}
                        </span>
                    </div>
                </ViewTableCell>
            );
        }

        return <ViewTableCell />;
    },
);
